import type {
  CommandData,
  OpenPostPosting,
  OpenPostPostingWrapper,
} from '@festina/life-general-types/life'
import {
  amountUtils,
  dateUtils,
  type Except,
  notEmpty,
  type Optional,
  stringUtils,
  translationUtils,
} from '@festina-life/core'
import { CircularSpinner } from '@festina-life/ui/feedback'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import {
  createColumnDefiner,
  type RowAction,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { Suspense, useMemo, useState } from 'react'

import { useLazyFetchLifeOpenPostPostingsForWorkPadIds } from '../../hooks/lifePostings/useLazyFetchLifeOpenPostPostingsForWorkPadIds'
import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { POSTING_DIMENSIONS_AND_KEYS } from '../../utility/postingUtils'
import { ErrorMessage } from '../errorFallback/ErrorMessage'
import { PostingVoucherModal } from '../postings/PostingVoucherModal'
import {
  hasOpenPostPostingVoucherRef,
  type OpenPostPostingWrapperWithSum,
} from './helpers'

const isCommandDataWithSequenceNo = (
  commandData: Optional<Except<CommandData, '_type'>>,
): commandData is CommandData & { postingSequenceNo: number } =>
  notEmpty(commandData) &&
  'postingSequenceNo' in commandData &&
  typeof commandData.postingSequenceNo === 'number'

type Props = {
  workPadIds: string[]
  shouldFetchPostingsForSeqNo?: boolean
  commandData?: Optional<CommandData>
}

const defineColumn = createColumnDefiner<OpenPostPostingWrapperWithSum>()

const EventOpenPostPostingsTableInternal = ({
  workPadIds,
  commandData,
  shouldFetchPostingsForSeqNo,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const [activePosting, setActivePosting] = useState<OpenPostPosting>()
  const [filteredTableData, setFilteredTableData] =
    useState<OpenPostPostingWrapper[]>()
  const { formatWithDecimals } = useAmountFormatter()
  const {
    postingWrappers: postingWrappersForWorkPadIds,
    isLoading: isLoadingPostingsForWorkPadIds,
    error: postingsForWorkPadIdsError,
  } = useLazyFetchLifeOpenPostPostingsForWorkPadIds(workPadIds)

  // Fetches an openPostPosting based on its sequence number, if the commandData has one
  const {
    data: openPostPostingWrapperCollection,
    isLoading: isLoadingPostingsForSeqNo,
    error: postingsForSeqNoError,
  } = useHook('fetchers.life.useFetchOpenPostPostings')(
    {
      tenantId,
      key1: isCommandDataWithSequenceNo(commandData) ? undefined : ownerId,
      postingSeqNo: isCommandDataWithSequenceNo(commandData)
        ? commandData.postingSequenceNo
        : undefined,
    },
    { enabled: !!shouldFetchPostingsForSeqNo },
  )

  const postingWrappersForSeqNo =
    openPostPostingWrapperCollection?.openPostPostingWrappers?.filter(
      notEmpty,
    ) ?? []

  const postingWrappers = postingWrappersForWorkPadIds?.concat(
    postingWrappersForSeqNo ?? [],
  )

  const satisfiedPostingWrappers = postingWrappers satisfies
    | OpenPostPostingWrapperWithSum[]
    | undefined

  const columns = useMemo(
    () => ({
      accountType: defineColumn({
        heading: translate('postings.column.accountType'),
        keyPath: 'openPostPosting.accountType',
        filterMode: 'auto',
      }),
      amount: defineColumn({
        heading: translate('postings.column.amount'),
        keyPath: 'openPostPosting.amount',
        formatter: formatWithDecimals,
        alignment: 'right',
      }),
      postingType: defineColumn({
        heading: translate('postings.column.postingType'),
        keyPath: 'openPostPosting.postingType',
        formatter: (type) => translateEnum('life.enum.PostingType', type),
        filterMode: 'auto',
      }),
      seqno: defineColumn({
        heading: translate('postings.column.seqno'),
        keyPath: 'openPostPosting.seqno',
        sortOptions: { sortType: 'asc' },
      }),
      transactionTime: defineColumn({
        heading: translate('postings.column.transactionTime'),
        keyPath: 'openPostPosting.transactionTime',
        formatter: dateUtils.dateAsStr,
        sortValue: (date) => date,
        filterMode: 'dateRange',
      }),
      truncated: defineColumn({
        heading: translate('postings.column.truncated'),
        keyPath: 'openPostPosting.truncated',
        formatter: (x) => translationUtils.translateBoolean(x, translate),
      }),
      valueDate: defineColumn({
        heading: translate('postings.column.valueDate'),
        keyPath: 'openPostPosting.valueDate',
        formatter: dateUtils.dateAsStr,
        sortValue: (date) => date,
      }),
      ...Object.fromEntries(
        POSTING_DIMENSIONS_AND_KEYS.map((dim) => [
          dim,
          defineColumn({
            heading: translateEnum('enum.PostingDimensions', dim),
            keyPath: `openPostPosting.dimensions.${dim}`,
            filterMode: 'auto',
          }),
        ]),
      ),
    }),
    [formatWithDecimals, translate, translateEnum],
  )

  const rowActions = (row: OpenPostPostingWrapperWithSum): RowAction[] => [
    {
      key: 'view',
      iconName: 'DocumentText' as const,
      title: translate('postings.column.voucher'),
      onClick:
        // In some cases it seems the voucher may be missing.
        // Attempting to open the voucher modal with no voucher would fail.
        hasOpenPostPostingVoucherRef(row)
          ? () => {
              setActivePosting(row.openPostPosting)
            }
          : undefined,
    },
  ]

  const postingFooter: OpenPostPostingWrapperWithSum[] = useMemo(() => {
    const postingWrappersData = filteredTableData ?? postingWrappers

    return [
      {
        _type: 'OpenPostPostingWrapper',
        openPostPosting: {
          _type: 'sumRow',
          accountType: translate('total'),
          amount:
            amountUtils.sumWithRounding(
              postingWrappersData?.map(
                (wrapper) => wrapper.openPostPosting?.amount,
              ),
            ) ?? 0,
        },
      },
    ]
  }, [filteredTableData, postingWrappers, translate])

  const handleTableData = (data: OpenPostPostingWrapper[]) => {
    // We need to compare the data to the filteredTableData to avoid an infinite render loop. This should be cleaned (Jira: LIFE-4435)
    if (
      stringUtils.hash(JSON.stringify(filteredTableData ?? [])) !==
      stringUtils.hash(JSON.stringify(data ?? []))
    ) {
      setFilteredTableData(data)
    }
  }

  return satisfiedPostingWrappers?.length ? (
    <>
      {activePosting && activePosting._id && (
        <Suspense>
          <PostingVoucherModal
            vouchersPostingType="OpenPostPosting"
            postingId={activePosting._id}
            sequenceNumber={activePosting.seqno}
            onClose={() => setActivePosting(undefined)}
          />
        </Suspense>
      )}
      <Card>
        <CardHeader title={translate('eventOpenPostPostingsTable.title')} />
        <CardBody>
          <SortableTable
            data={satisfiedPostingWrappers}
            footerData={postingFooter}
            columns={columns}
            onPresentDataChange={handleTableData}
            rowActions={rowActions}
          />
        </CardBody>
      </Card>
    </>
  ) : (
    <>
      {postingsForWorkPadIdsError && (
        <ErrorMessage error={postingsForWorkPadIdsError} />
      )}
      {postingsForSeqNoError && <ErrorMessage error={postingsForSeqNoError} />}
      {(isLoadingPostingsForWorkPadIds || isLoadingPostingsForSeqNo) && (
        <CircularSpinner />
      )}
    </>
  )
}

export const EventOpenPostPostingsTable = withLifeErrorBoundary(
  withSuspense(EventOpenPostPostingsTableInternal),
)
